import { get } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type Store from '@ember-data/store';
import Component from '@glimmer/component';
import CopyButton from 'ember-cli-clipboard/components/copy-button';
import { dropTask } from 'ember-concurrency';
import { eq } from 'ember-truth-helpers';
import KioskDemoPagesFinalScreen from 'garaje/components/homepage/kiosk-demo/pages/final-screen';
import KioskDemoPagesHostSelection from 'garaje/components/homepage/kiosk-demo/pages/host-selection';
import KioskDemoPagesLegalDocs from 'garaje/components/homepage/kiosk-demo/pages/legal-docs';
import KioskDemoPagesPhoto from 'garaje/components/homepage/kiosk-demo/pages/photo';
import KioskDemoPagesSignInFields from 'garaje/components/homepage/kiosk-demo/pages/sign-in-fields';
import KioskDemoPagesSignInFlow from 'garaje/components/homepage/kiosk-demo/pages/sign-in-flow';
import KioskDemoPagesVisitorsRecognition from 'garaje/components/homepage/kiosk-demo/pages/visitors-recognition';
import KioskDemoPagesWelcome from 'garaje/components/homepage/kiosk-demo/pages/welcome';
import type { KioskDemoStep } from 'garaje/components/homepage/kiosk-demo/provider';
import KioskDemoProvider from 'garaje/components/homepage/kiosk-demo/provider';
import config from 'garaje/config/environment';
import type CurrentAdminService from 'garaje/services/current-admin';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import type ImpressionsService from 'garaje/services/impressions';
import type SessionService from 'garaje/services/session';
import type StateService from 'garaje/services/state';
import type StatsigService from 'garaje/services/statsig';
import { IMPRESSION_NAMES } from 'garaje/utils/enums';
import { tracked } from 'tracked-built-ins';

const SHARE_TEXT = 'Copy share link';
const SHARE_LINK_COPIED_TEXT = 'Link copied';
const LINK_IMG_SRC = '/assets/images/ipad-demo/icons/chain.svg';
const LINK_COPIED_IMG_SRC = '/assets/images/ipad-demo/icons/check.svg';

interface SignInKioskDemoArgs {
  isDemoInstance: boolean;
  onExternalClose?: () => void;
  onComplete?: () => void;
  demoName: string;
}

export default class SignInKioskDemo extends Component<SignInKioskDemoArgs> {
  @service declare state: StateService;
  @service declare statsig: StatsigService;
  @service declare store: Store;
  @service declare session: SessionService;
  @service declare impressions: ImpressionsService;
  @service declare featureFlags: FeatureFlagsService;
  @service declare currentAdmin: CurrentAdminService;

  @tracked copyShareLinkText: string = SHARE_TEXT;
  @tracked copyShareLinkImageSrc: string = LINK_IMG_SRC;

  constructor(owner: unknown, args: SignInKioskDemoArgs) {
    super(owner, args);

    if (this.isUserLoggedIn && !this.args.isDemoInstance) {
      this.store
        .query('homepage-group', {
          include: 'homepage-item-group-mappings.homepage-item.setup-guide-items',
        })
        .then(() => null)
        .catch(() => null);
    }
  }

  get shareLink(): string {
    const { currentCompany } = this.state;
    if (config.environment === 'development') {
      return `https://dashboard.envoy.dev/ipad-demo?cid=${currentCompany?.id}`;
    } else if (config.environment === 'staging') {
      return `https://dashboard.envoy.christmas/ipad-demo?cid=${currentCompany?.id}`;
    } else {
      return `https://dashboard.envoy.com/ipad-demo?cid=${currentCompany?.id}`;
    }
  }

  get isUserLoggedIn(): boolean {
    return this.session?.isAuthenticated;
  }

  get showShareModal(): boolean {
    return this.currentAdmin.isGlobalAdmin && this.featureFlags.isEnabled('growth_preview_bulk_add_admins');
  }

  completeSetupGuideStep = dropTask(async () => {
    if (this.isUserLoggedIn && !this.args.isDemoInstance) {
      try {
        await this.impressions.postImpression.perform(IMPRESSION_NAMES.KIOSK_DEMO_IPAD_SIGNIN);
        const groups = await this.store.query('homepage-group', {
          include: 'homepage-item-group-mappings.homepage-item.setup-guide-items',
        });

        const setupGuideGroup = groups
          ?.find(
            (groupItem) => groupItem.identifier === 'configuration_a' || groupItem.identifier === 'configuration_e',
          )
          ?.sortedMappings.filter((sm) => sm.homepageItem.identifier === 'setup_guide_items');

        if (setupGuideGroup && setupGuideGroup.length > 0) {
          const seeWhatVisitorsSeeTask = setupGuideGroup[0]?.homepageItem?.setupGuideItems?.find(
            (item) => item.identifier === 'visitors-demo',
          );
          if (seeWhatVisitorsSeeTask) {
            seeWhatVisitorsSeeTask.completed = true;
            seeWhatVisitorsSeeTask
              .save()
              .then(() => null)
              .catch(() => null);
          }
        }
      } catch {
        return;
      }
    }
  });

  @action copyShareLink(): void {
    this.statsig.logEvent(`kiosk-demo-${this.args.demoName}-share-link-clicked`);
    this.copyShareLinkText = SHARE_LINK_COPIED_TEXT;
    this.copyShareLinkImageSrc = LINK_COPIED_IMG_SRC;

    setTimeout(() => {
      this.copyShareLinkText = SHARE_TEXT;
      this.copyShareLinkImageSrc = LINK_IMG_SRC;
    }, 2000);
  }

  steps: Array<KioskDemoStep> = [
    {
      imageSrc: '/assets/images/ipad-demo/Step1.svg',
      imageAlt: 'Image of welcome page',
      infoComponentName: 'Homepage::KioskDemo::Pages::Welcome',
    },
    {
      imageSrc: '/assets/images/ipad-demo/Step2.svg',
      imageAlt: 'Image of visitors',
      infoComponentName: 'Homepage::KioskDemo::Pages::VisitorsRecognition',
    },
    {
      imageSrc: '/assets/images/ipad-demo/Step3.svg',
      imageAlt: 'Image of sign in flow',
      infoComponentName: 'Homepage::KioskDemo::Pages::SignInFlow',
    },
    {
      imageSrc: '/assets/images/ipad-demo/Step4.svg',
      imageAlt: 'Image of sign in field',
      infoComponentName: 'Homepage::KioskDemo::Pages::SignInFields',
    },
    {
      imageSrc: '/assets/images/ipad-demo/Step5.svg',
      imageAlt: 'Image of add host',
      infoComponentName: 'Homepage::KioskDemo::Pages::HostSelection',
    },
    {
      imageSrc: '/assets/images/ipad-demo/Step06.png',
      imageAlt: 'Image of NDA',
      infoComponentName: 'Homepage::KioskDemo::Pages::LegalDocs',
    },
    {
      imageSrc: '/assets/images/ipad-demo/Step07.png',
      imageAlt: 'Image of guest photo',
      infoComponentName: 'Homepage::KioskDemo::Pages::Photo',
    },
    {
      imageSrc: '/assets/images/ipad-demo/Step08.png',
      imageAlt: 'Image of party',
      infoComponentName: 'Homepage::KioskDemo::Pages::FinalScreen',
    },
  ];

  <template>
    <KioskDemoProvider
      @stepArray={{this.steps}}
      @isDemoInstance={{@isDemoInstance}}
      @onExternalClose={{@onExternalClose}}
      @onComplete={{this.completeSetupGuideStep.perform}}
      @demoName={{@demoName}}
      as |provider|
    >
      <div class="flex flex-col h-[798px]" data-test-ipad-demo-signin-modal>
        <div class="flex flex-row items-center p-7 w-full">
          <div class="text-carbon70 text-[24px] font-bold">See Envoy in Action</div>
          {{#unless @isDemoInstance}}
            <div class="flex items-center gap-[16px] ml-auto">
              {{#if this.showShareModal}}

                <button
                  {{on "click" provider.toggleShareModal}}
                  class="btn btn--outline"
                  data-test-kiosk-demo-share-button
                >Share</button>
              {{else}}
                <CopyButton @clipboardText={{this.shareLink}} @success={{this.copyShareLink}} class="btn btn--outline">
                  <span class="flex items-center">
                    <img
                      class="pb-[2px] pr-[5px]"
                      src={{this.copyShareLinkImageSrc}}
                      alt="link img"
                      data-test-kiosk-demo-copy-link
                    />
                    <span>{{this.copyShareLinkText}}</span>
                  </span>
                </CopyButton>
              {{/if}}
              <button
                {{on "click" provider.toggleShowModal}}
                class="btn btn--outline btn--cancel h-[42px] w-[42px]"
                data-test-kiosk-demo-close
              ><span><img
                    class="max-w-[16px] max-h-[16px] align-middle"
                    src="/assets/images/ipad-demo/icons/close.svg"
                    alt="x"
                  /></span></button>
            </div>
          {{/unless}}
        </div>
        <div class="flex flex-row h-full">
          <div class="flex flex-col w-[737px] max-w-[737px]">
            <div class="h-1 bg-carbon-10">
              <div class="h-1 bg-red-50 transition-[width] duration-300" style={{escape provider.progressBarStyle}} />
            </div>
            <div class="flex bg-carbon-5 h-full items-center justify-center pb-[40px] pl-[60px]">
              {{#if
                (eq
                  (get (get this.steps provider.currentStep) "infoComponentName") "Homepage::KioskDemo::Pages::Welcome"
                )
              }}
                <img
                  class="w-[390px] h-[515px] animated fadeIn cursor-pointer"
                  {{on "click" provider.onNext}}
                  src={{get (get this.steps provider.currentStep) "imageSrc"}}
                  alt={{get (get this.steps provider.currentStep) "imageAlt"}}
                />
              {{/if}}
              {{#if
                (eq
                  (get (get this.steps provider.currentStep) "infoComponentName")
                  "Homepage::KioskDemo::Pages::VisitorsRecognition"
                )
              }}
                <img
                  class="w-[390px] h-[515px] animated fadeIn cursor-pointer"
                  {{on "click" provider.onNext}}
                  src={{get (get this.steps provider.currentStep) "imageSrc"}}
                  alt={{get (get this.steps provider.currentStep) "imageAlt"}}
                />
              {{/if}}
              {{#if
                (eq
                  (get (get this.steps provider.currentStep) "infoComponentName")
                  "Homepage::KioskDemo::Pages::SignInFlow"
                )
              }}
                <img
                  class="w-[390px] h-[515px] animated fadeIn cursor-pointer"
                  {{on "click" provider.onNext}}
                  src={{get (get this.steps provider.currentStep) "imageSrc"}}
                  alt={{get (get this.steps provider.currentStep) "imageAlt"}}
                />
              {{/if}}
              {{#if
                (eq
                  (get (get this.steps provider.currentStep) "infoComponentName")
                  "Homepage::KioskDemo::Pages::SignInFields"
                )
              }}
                <img
                  class="w-[390px] h-[515px] animated fadeIn cursor-pointer"
                  {{on "click" provider.onNext}}
                  src={{get (get this.steps provider.currentStep) "imageSrc"}}
                  alt={{get (get this.steps provider.currentStep) "imageAlt"}}
                />
              {{/if}}
              {{#if
                (eq
                  (get (get this.steps provider.currentStep) "infoComponentName")
                  "Homepage::KioskDemo::Pages::HostSelection"
                )
              }}
                <img
                  class="w-[390px] h-[515px] animated fadeIn cursor-pointer"
                  {{on "click" provider.onNext}}
                  src={{get (get this.steps provider.currentStep) "imageSrc"}}
                  alt={{get (get this.steps provider.currentStep) "imageAlt"}}
                />
              {{/if}}
              {{#if
                (eq
                  (get (get this.steps provider.currentStep) "infoComponentName")
                  "Homepage::KioskDemo::Pages::LegalDocs"
                )
              }}
                <img
                  class="w-[390px] h-[515px] animated fadeIn cursor-pointer"
                  {{on "click" provider.onNext}}
                  src={{get (get this.steps provider.currentStep) "imageSrc"}}
                  alt={{get (get this.steps provider.currentStep) "imageAlt"}}
                />
              {{/if}}
              {{#if
                (eq (get (get this.steps provider.currentStep) "infoComponentName") "Homepage::KioskDemo::Pages::Photo")
              }}
                <img
                  class="w-[390px] h-[515px] animated fadeIn cursor-pointer"
                  {{on "click" provider.onNext}}
                  src={{get (get this.steps provider.currentStep) "imageSrc"}}
                  alt={{get (get this.steps provider.currentStep) "imageAlt"}}
                />
              {{/if}}
              {{#if
                (eq
                  (get (get this.steps provider.currentStep) "infoComponentName")
                  "Homepage::KioskDemo::Pages::FinalScreen"
                )
              }}
                <img
                  class="w-[390px] h-[515px] animated fadeIn"
                  src={{get (get this.steps provider.currentStep) "imageSrc"}}
                  alt={{get (get this.steps provider.currentStep) "imageAlt"}}
                />
              {{/if}}
            </div>
          </div>
          <div class="flex flex-col h-full border-t-[1px] min-w-[300px] w-[360px]">
            {{#if
              (eq (get (get this.steps provider.currentStep) "infoComponentName") "Homepage::KioskDemo::Pages::Welcome")
            }}
              <KioskDemoPagesWelcome
                @currentStep={{provider.currentStep}}
                @totalStepCount={{provider.totalStepCount}}
                @isUserLoggedIn={{provider.isUserLoggedIn}}
                @demoName={{@demoName}}
                @isDemoInstance={{@isDemoInstance}}
              />
            {{/if}}
            {{#if
              (eq
                (get (get this.steps provider.currentStep) "infoComponentName")
                "Homepage::KioskDemo::Pages::VisitorsRecognition"
              )
            }}
              <KioskDemoPagesVisitorsRecognition
                @currentStep={{provider.currentStep}}
                @totalStepCount={{provider.totalStepCount}}
                @isUserLoggedIn={{provider.isUserLoggedIn}}
                @demoName={{@demoName}}
                @isDemoInstance={{@isDemoInstance}}
              />
            {{/if}}
            {{#if
              (eq
                (get (get this.steps provider.currentStep) "infoComponentName") "Homepage::KioskDemo::Pages::SignInFlow"
              )
            }}
              <KioskDemoPagesSignInFlow
                @currentStep={{provider.currentStep}}
                @totalStepCount={{provider.totalStepCount}}
                @isUserLoggedIn={{provider.isUserLoggedIn}}
                @demoName={{@demoName}}
                @isDemoInstance={{@isDemoInstance}}
              />
            {{/if}}
            {{#if
              (eq
                (get (get this.steps provider.currentStep) "infoComponentName")
                "Homepage::KioskDemo::Pages::SignInFields"
              )
            }}
              <KioskDemoPagesSignInFields
                @currentStep={{provider.currentStep}}
                @totalStepCount={{provider.totalStepCount}}
                @isUserLoggedIn={{provider.isUserLoggedIn}}
                @firstFlowId={{provider.firstFlowId}}
                @demoName={{@demoName}}
                @isDemoInstance={{@isDemoInstance}}
              />
            {{/if}}
            {{#if
              (eq
                (get (get this.steps provider.currentStep) "infoComponentName")
                "Homepage::KioskDemo::Pages::HostSelection"
              )
            }}
              <KioskDemoPagesHostSelection
                @currentStep={{provider.currentStep}}
                @totalStepCount={{provider.totalStepCount}}
                @isUserLoggedIn={{provider.isUserLoggedIn}}
                @demoName={{@demoName}}
                @isDemoInstance={{@isDemoInstance}}
              />
            {{/if}}
            {{#if
              (eq
                (get (get this.steps provider.currentStep) "infoComponentName") "Homepage::KioskDemo::Pages::LegalDocs"
              )
            }}
              <KioskDemoPagesLegalDocs
                @currentStep={{provider.currentStep}}
                @totalStepCount={{provider.totalStepCount}}
                @isUserLoggedIn={{provider.isUserLoggedIn}}
                @firstFlowId={{provider.firstFlowId}}
                @demoName={{@demoName}}
                @isDemoInstance={{@isDemoInstance}}
              />
            {{/if}}
            {{#if
              (eq (get (get this.steps provider.currentStep) "infoComponentName") "Homepage::KioskDemo::Pages::Photo")
            }}
              <KioskDemoPagesPhoto
                @currentStep={{provider.currentStep}}
                @totalStepCount={{provider.totalStepCount}}
                @isUserLoggedIn={{provider.isUserLoggedIn}}
                @firstFlowId={{provider.firstFlowId}}
                @demoName={{@demoName}}
                @isDemoInstance={{@isDemoInstance}}
              />
            {{/if}}
            {{#if
              (eq
                (get (get this.steps provider.currentStep) "infoComponentName")
                "Homepage::KioskDemo::Pages::FinalScreen"
              )
            }}
              <KioskDemoPagesFinalScreen
                @currentStep={{provider.currentStep}}
                @totalStepCount={{provider.totalStepCount}}
                @isUserLoggedIn={{provider.isUserLoggedIn}}
                @demoName={{@demoName}}
                @isDemoInstance={{@isDemoInstance}}
              />
            {{/if}}
            <div class="flex flex-row mt-auto p-8">
              {{#if provider.isPreviousStep}}
                <button class="btn btn--outline" {{on "click" provider.onPrevious}} data-test-kiosk-demo-previous>
                  <span class="flex items-center text-lg pb-[1px]"><img
                      class="pb-[2px] pr-[10px]"
                      src="/assets/images/ipad-demo/icons/chevron-left.svg"
                      alt="Chevron left"
                    />Back</span>
                </button>
              {{/if}}
              {{#if provider.isNextStep}}
                <div class="ml-auto">
                  <button class="btn btn--brand" {{on "click" provider.onNext}} data-test-kiosk-demo-next>
                    <span class="flex items-center text-lg pb-[1px]">Next<img
                        class="pb-[2px] pl-[15px]"
                        src="/assets/images/ipad-demo/icons/chevron-right.svg"
                        alt="Chevron right"
                      /></span>
                  </button>
                </div>
              {{/if}}
            </div>
          </div>
        </div>
      </div>
    </KioskDemoProvider>
  </template>
}
